import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { useNavigate, Link } from 'react-router-dom';
import { createSelector } from 'reselect';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { deleteBeachCleaning, getBeachCleaningList } from '../../slices/thunks';
import { ODataGrid } from '../../Components/OElements/ODataGrid';
import { Icons } from '../../utils/Icons';

const BeachCleaningsList = () => {
	const dispatch = useDispatch();
	const history = useNavigate();

	const selectorBeachCleanings = createSelector(
		state => state.BeachCleanings,
		beachCleaning => ({
			beachCleaningList: beachCleaning.beachCleaningList,
			isLoadingList: beachCleaning.isLoadingList,
			isBeachCleaningSuccess: beachCleaning.isBeachCleaningSuccess,
			error: beachCleaning.error,
			isAdding: beachCleaning.isAdding,
			isAdded: beachCleaning.isAdded,
			isAddError: beachCleaning.isAddError,
			isUpdating: beachCleaning.isUpdating,
			isUpdated: beachCleaning.isUpdated,
			isUpdateError: beachCleaning.isUpdateError,
			isDeleting: beachCleaning.isDeleting,
			isDeleted: beachCleaning.isDeleted,
			isDeleteError: beachCleaning.isDeleteError,
		})
	);

	const { beachCleaningList, isLoadingList } = useSelector(selectorBeachCleanings);
	const [dataBeachCleaning, setDataBeachCleaning] = useState(null);
	const [modalDelete, setModalDelete] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const formatDate = useCallback((dateString) => {
		if (!dateString) return '-';
		const date = dayjs(dateString);
		return date.isValid() ? date.format('DD-MM-YYYY') : '-';
	}, []);

	useEffect(() => {
		dispatch(getBeachCleaningList());
	}, [dispatch]);

	// Update Data
	const handleClickEdit = useCallback((beachCleaning) => {
		history(`/residue/beach-cleanings/${beachCleaning._id}`);
	}, [history]);

	const handleCreateBeachCleaning = useCallback(() => {
		history('/residue/beach-cleanings/register');
	}, [history]);

	const handleClickDelete = useCallback((beachCleaning) => {
		setDataBeachCleaning(beachCleaning);
		setModalDelete(true);
	}, []);

	const handleToggleModalDelete = useCallback(() => {
		setModalDelete(prev => !prev);
		if (modalDelete) {
			setDataBeachCleaning(null);
		}
	}, [modalDelete]);

	const handleConfirmDelete = useCallback(async () => {
		if (!dataBeachCleaning?._id) return;
		try {
			setIsDeleting(true);
			await dispatch(deleteBeachCleaning(dataBeachCleaning)).unwrap();
			handleToggleModalDelete();
			dispatch(getBeachCleaningList());
		} finally {
			setIsDeleting(false);
		}
	}, [dataBeachCleaning, dispatch, handleToggleModalDelete]);

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: cellProps => {
					const rowData = cellProps.row.original;
					return (
						<React.Fragment>
							<div className='d-flex justify-content-center'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										<li className='list-inline-item'>
											<Link
												title={'Editar'}
												to={`/residue/beach-cleanings/${rowData._id}`}
											>
												<i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
											</Link>
										</li>
										<li className='list-inline-item'>
											<Link
												title={'Eliminar'}
												to='#'
												className='remove-item-btn'
												onClick={event => {
													event.preventDefault();
													handleClickDelete(rowData);
												}}
											>
												<i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},
			{
				header: 'Sector', 
			  	accessorKey: 'sector.name', 
				size: 'auto' 
			},
			{
				header: 'Fecha',
				accessorKey: 'date',
				size: 'auto',
				dataType: 'date',
				defaultSort: 'desc',
				Cell: cellProps => <span>{formatDate(cellProps.row.original.date)}</span>,
			},
			{
				header: '¿Limpieza realizada?',
				accessorKey: 'is_done',
				size: 'auto',
				dataType: 'boolean',
				Cell: ({ row }) => {
					return (
						<input
							className='form-check-input'
							role='switch'
							type='checkbox'
							id='disabled'
							name='disabled'
							disabled
							checked={row.original.is_done}
						/>
					);
				},
			},
			{
				header: 'Motivo',
				accessorKey: 'beach_cleaning_reason.name',
				size: 'auto',
			},
			{
				header: 'Kilómetros recorridos',
				accessorKey: 'kilometers_traveled',
				size: 'auto',
				dataType: 'float.1',
			},

		],
		[formatDate, handleClickEdit]
	);

	return (
		<React.Fragment>
			<Col lg={12}>
				<div className='card'>
					<div className='card-body pt-3'>
						<ODataGrid
							columns={columns}
							data={beachCleaningList || []}
							isLoadingData={isLoadingList}
							nameFile={'limpiezas_de_playa'}
							customToolbarActions={[
								{
									label: 'Crear',
									icon: Icons.plus,
									onClick: handleCreateBeachCleaning,
									title: 'Registrar Limpieza de Playa',
								},
							]}
						/>
					</div>
				</div>
			</Col>

			<Modal
				isOpen={modalDelete}
				toggle={handleToggleModalDelete}
				size='sm'
				className='border-0'
				modalClassName='modal fade zoomIn'
				backdrop='static'
				centered
			>
				<ModalHeader className='modal-title' toggle={handleToggleModalDelete} />
				<ModalBody className='text-center p-3'>
					<lord-icon
						src='https://cdn.lordicon.com/tdrtiskw.json'
						trigger='loop'
						colors='primary:#f06548,secondary:#405189'
						style={{ width: '90px', height: '90px' }}
					></lord-icon>
					<div className='mt-2'>
						<h3 className='mb-3'>
							¡Estás a punto de eliminar una limpieza de playa!
						</h3>
						<p className='text-muted mb-4'>
							Si deseas realizar esta acción debes hacer click en confirmar.
						</p>
						<p className='small'>
							Sector: {dataBeachCleaning?.sector?.name || '-'}
						</p>
						<div className='hstack gap-2 justify-content-center'>
							<Button className='btn-danger btn-label' onClick={handleToggleModalDelete}>
								<i className='icon-x label-icon'></i> Cancelar
							</Button>
							<button className='btn btn-label btn-primary' onClick={handleConfirmDelete} disabled={isDeleting}>
								{isDeleting ? (
									<i className='label-icon'><Spinner size='sm' /></i>
								) : (
									<i className='ri-check-double-line label-icon'></i>
								)}
								Confirmar
							</button>
						</div>
					</div>
				</ModalBody>
			</Modal>
		</React.Fragment>
	);
};

export default BeachCleaningsList;
